import React from 'react';
import PlayIcon from '@assets/icons/play.svg';
import type { Variant } from '@components';
import { BaseButton } from '@components';
import { BORDER_COLORS, SURFACE_COLORS } from '@constants/colors';
import { BodySmSmMedium } from '@constants/text.ts';
import { useAppSelector } from '@hooks/reduxHooks.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';
import CardProgressBar from '@routes/sidebar/___shared___/components/card-progress-bar';
import { getLearningStatus } from '@routes/sidebar/___shared___/utils.ts';
import type { LearningResponse } from '@routes/sidebar/learning/api/learning.api.ts';
import {
    CardImage,
    CarouselCard,
    CarouselImageInfo,
    CarouselProgressInfo,
    CreatorAndProggress,
} from '@routes/sidebar/learning/styled.tsx';
import CardInfo from '@routes/sidebar/learning/ui/learning-tab/card-info';
import CardPillLabels from '@routes/sidebar/learning/ui/learning-tab/card-pill-labels';
import { ProgressTimeLeft } from '@routes/sidebar/notifications/api/notifications.api.ts';
import { selectServerTimeDate } from '@utils/serverTime.ts';

interface Props {
    learning: LearningResponse;
    onBookmarkClick: (learning: LearningResponse, e?: React.MouseEvent) => void;
}

const LearningCard: React.FC<Props> = ({ learning, onBookmarkClick }) => {
    const { t } = useLingui();
    const navigate = useCustomNavigate();

    const serverTimeDate = useAppSelector(selectServerTimeDate) ?? new Date();

    const { isCompleted, isDueSoon, isOverdue } = getLearningStatus(learning, serverTimeDate);

    let buttonText = t`Start`;
    if (learning.userActivity !== null && learning.userActivity?.progress.progressPercent > 0) {
        buttonText = t`Resume now`;
        if (isCompleted && isDueSoon) {
            buttonText = t`Re-do`;
        } else if (isCompleted && isOverdue) {
            buttonText = t`Start`;
        } else if (isCompleted) {
            buttonText = t`Completed`;
        }
    }

    let buttonVariant: Variant;
    if (isOverdue) {
        buttonVariant = 'primary';
    } else if (isDueSoon) {
        buttonVariant = 'warning';
    } else if (isCompleted) {
        buttonVariant = 'success';
    } else {
        buttonVariant = 'primary';
    }

    let colors;
    if (!isOverdue && isDueSoon) {
        colors = {
            progressColor: BORDER_COLORS.warning,
            bgColor: SURFACE_COLORS.warning,
        };
    } else if (!isOverdue && isCompleted) {
        colors = {
            progressColor: BORDER_COLORS.success,
            bgColor: SURFACE_COLORS.action,
        };
    }

    const progressPercent =
        isCompleted && isOverdue ? '0' : (learning.userActivity?.progress?.progressPercent?.toString() ?? '0');

    return (
        <CarouselCard data-cy="learning-card">
            <CarouselImageInfo>
                <CardImage src={learning.thumbNailURI || undefined} />
                <CardPillLabels learning={learning} serverTime={serverTimeDate} />
                <CardInfo learning={learning} onBookmarkClick={onBookmarkClick} serverTime={serverTimeDate} />
            </CarouselImageInfo>
            <CarouselProgressInfo>
                {!!learning.userActivity && (
                    <CardProgressBar
                        minLeft={ProgressTimeLeft(learning.userActivity?.progress)}
                        percentComplete={progressPercent}
                        isCustomText={false}
                        progressColor={colors?.progressColor}
                        bgColor={colors?.bgColor}
                    />
                )}
                <CreatorAndProggress $isCompleted={isCompleted}>
                    <BodySmSmMedium>{learning.learningDetails.creator}</BodySmSmMedium>
                    <BaseButton
                        data-cy="learning-card-button"
                        icon={<PlayIcon />}
                        iconPlacement="end"
                        $customType={buttonVariant}
                        onClick={() => {
                            navigate(`${ROUTES.SIDEBAR.LEARNING}/${learning.id}`);
                        }}
                    >
                        {buttonText}
                    </BaseButton>
                </CreatorAndProggress>
            </CarouselProgressInfo>
        </CarouselCard>
    );
};

export default LearningCard;
